import { useForm, router } from '@inertiajs/react'
import { useState } from 'react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { SectionHeader } from '@/components/backoffice/SectionHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ListChecks, Trash2 } from 'lucide-react'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { StaffMember } from '@/types'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type Props = { staff: StaffMember[]; search: string; pagination: PaginationMeta }

const ACCESS_LEVELS = [
  { key: 'admin' as const, label: 'Admin' },
  { key: 'compliance' as const, label: 'Compliance' },
  { key: 'support' as const, label: 'Support' },
]

function AccessToggle({ staff, accessKey }: { staff: StaffMember; accessKey: 'admin' | 'compliance' | 'support' }) {
  const hasAccess = staff[accessKey]
  const acceptsAssignmentsKey = `${accessKey}_accepts_assignments` as keyof StaffMember
  const acceptsAssignments = staff[acceptsAssignmentsKey] as boolean | undefined

  const handleToggleAccess = () => {
    if (hasAccess) {
      router.delete(`/backoffice/staff/${staff.id}/${accessKey}_access`, { preserveScroll: true })
    } else {
      router.post(`/backoffice/staff/${staff.id}/${accessKey}_access`, {}, { preserveScroll: true })
    }
  }

  const handleToggleAssignments = () => {
    if (!hasAccess) return
    router.put(`/backoffice/staff/${staff.id}/${accessKey}_access`, {
      [`${accessKey}_access`]: { accepts_assignments: !acceptsAssignments }
    }, { preserveScroll: true })
  }

  return (
    <div className="flex min-w-16 flex-col items-center gap-1">
      <button
        type="button"
        onClick={handleToggleAccess}
        title={hasAccess ? `Revoke ${accessKey}` : `Grant ${accessKey}`}
        className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Badge
          variant={hasAccess ? 'default' : 'outline'}
          className={cn("cursor-pointer text-[11px] transition-colors", !hasAccess && "opacity-40 hover:opacity-70")}
        >
          {accessKey === 'admin' ? 'Admin' : accessKey === 'compliance' ? 'CO' : 'SO'}
        </Badge>
      </button>
      {hasAccess && (accessKey === 'compliance' || accessKey === 'support') && (
        <button
          type="button"
          onClick={handleToggleAssignments}
          className={cn(
            "cursor-pointer rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            acceptsAssignments ? "border-green-200 bg-green-100 text-green-700 hover:bg-green-200" : "border-red-200 bg-red-100 text-red-700 hover:bg-red-200"
          )}
          title={acceptsAssignments ? "Currently active for assignments. Click to mark inactive." : "Currently inactive for assignments. Click to activate."}
        >
          {acceptsAssignments ? 'Active' : 'Inactive'}
        </button>
      )}
    </div>
  )
}

function AddStaffForm() {
  const form = useForm({ staff: { email_address: '', access_level: 'support' } })

  return (
    <div className="border-b border-border/40 pb-5">
      <form onSubmit={e => { e.preventDefault(); form.post('/backoffice/staff', { preserveScroll: true, onSuccess: () => form.reset() }) }} className="flex items-end gap-3">
        <div className="flex-1 max-w-xs">
          <label htmlFor="email" className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Email</label>
          <Input
            id="email"
            type="email"
            placeholder="user@example.com"
            value={form.data.staff.email_address}
            onChange={e => form.setData('staff', { ...form.data.staff, email_address: e.target.value })}
            required
            className="h-8 text-sm"
          />
        </div>
        <div className="w-32">
          <label htmlFor="access_level" className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Level</label>
          <select
            id="access_level"
            value={form.data.staff.access_level}
            onChange={e => form.setData('staff', { ...form.data.staff, access_level: e.target.value })}
            className="flex h-9 w-full items-center rounded-md border border-input bg-transparent pl-3 pr-8 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {ACCESS_LEVELS.map(level => <option key={level.key} value={level.key}>{level.label}</option>)}
          </select>
        </div>
        <Button type="submit" size="sm" variant="outline" disabled={form.processing} className="h-8 text-[13px]">
          Grant Access
        </Button>
      </form>
      {form.errors && Object.keys(form.errors).length > 0 && (
        <p className="text-sm text-destructive mt-2">{Object.values(form.errors).flat().join(', ')}</p>
      )}
    </div>
  )
}

export default function StaffIndex({ staff, search, pagination }: Props) {
  const [staffToRemove, setStaffToRemove] = useState<StaffMember | null>(null)
  const [removing, setRemoving] = useState(false)

  function TooltipLabel({ label, children }: { label: string; children: React.ReactNode }) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    )
  }

  function Abbreviation({ short, label }: { short: string; label: string }) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} aria-label={label} className="cursor-help rounded-sm underline decoration-dotted underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {short}
          </span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    )
  }

  function countLink(staffId: string, label: string, value: number, params: string) {
    return (
      <button
        type="button"
        onClick={() => router.visit(`/backoffice/staff/${staffId}/assignments?${params}`)}
        className="min-h-7 rounded-sm text-left text-[11px] text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {label}: <span className="font-medium text-foreground">{value}</span>
      </button>
    )
  }

  function removeStaff() {
    if (!staffToRemove || removing) return

    setRemoving(true)
    router.delete(`/backoffice/staff/${staffToRemove.id}`, {
      preserveScroll: true,
      onSuccess: () => setStaffToRemove(null),
      onFinish: () => setRemoving(false),
    })
  }

  return (
    <BackofficeLayout title="Staff" contentWidth="wide" breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Staff' }]}>
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Staff Management</h1>
          <p className="text-sm text-muted-foreground">Manage backoffice access for platform staff</p>
        </div>

        <AddStaffForm />

        <SearchInput value={search} placeholder="Search by email..." />

        <div>
          <SectionHeader title="Staff Members" count={pagination.total} />
          <div className="overflow-x-auto rounded-lg border">
            <div className="hidden min-w-[920px] grid-cols-[minmax(240px,1fr)_240px_320px_96px] items-center border-b border-border/60 px-4 py-2 text-[12px] font-medium uppercase tracking-wider text-muted-foreground md:grid">
              <div>Email</div>
              <div className="text-center">Access</div>
              <div>
                Assignments (
                <Abbreviation short="CO" label="Compliance Officer" />
                <span aria-hidden="true"> / </span>
                <Abbreviation short="SO" label="Support Officer" />
                )
              </div>
              <div className="text-right">Actions</div>
            </div>

            {staff.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-sm text-muted-foreground">No staff members yet. Add one above.</p>
              </div>
            ) : (
              <div className="divide-y divide-border/40">
                {staff.map(member => {
                  const counts = member.assignment_counts
                  const totalAssignments = counts?.total_active || 0

                  return (
                    <div key={member.id} className="grid gap-3 px-4 py-3 md:min-w-[920px] md:grid-cols-[minmax(240px,1fr)_240px_320px_96px] md:items-center">
                      <div className="min-w-0">
                        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground md:hidden">Email</div>
                        <div className="break-words text-sm font-medium text-foreground md:truncate">{member.email_address}</div>
                      </div>

                      <div>
                        <div className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground md:hidden">Access</div>
                        <div className="flex flex-wrap gap-3 md:justify-center">
                          {ACCESS_LEVELS.map(level => (
                            <AccessToggle key={level.key} staff={member} accessKey={level.key} />
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground md:hidden">
                          Assignments
                        </div>
                        {counts ? (
                          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                            {countLink(member.id, 'Biz CO', counts.business_compliance, 'record_type=business&role=compliance')}
                            {countLink(member.id, 'Biz SO', counts.business_support, 'record_type=business&role=support')}
                            {countLink(member.id, 'Form CO', counts.formation_compliance, 'record_type=formation&role=compliance')}
                            {countLink(member.id, 'Form SO', counts.formation_support, 'record_type=formation&role=support')}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </div>

                      <div>
                        <div className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground md:hidden">Actions</div>
                        <div className="flex gap-1 md:justify-end">
                          <TooltipLabel label="Manage or reassign assignments">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => router.visit(`/backoffice/staff/${member.id}/assignments`)}
                              className="h-9 w-9 text-muted-foreground hover:text-foreground"
                              aria-label={`Manage assignments for ${member.email_address}`}
                            >
                              <ListChecks className="w-4 h-4" />
                            </Button>
                          </TooltipLabel>
                          <TooltipLabel label="Remove staff member">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setStaffToRemove(member)}
                              className="h-9 w-9 text-muted-foreground hover:text-destructive"
                              aria-label={`Remove staff member ${member.email_address}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TooltipLabel>
                        </div>
                        {totalAssignments > 0 && (
                          <p className="mt-1 text-[11px] text-muted-foreground md:text-right">
                            {totalAssignments} assigned
                          </p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <Pagination pagination={pagination} />
        </div>
      </div>

      <Dialog open={staffToRemove !== null} onOpenChange={open => { if (!open && !removing) setStaffToRemove(null) }}>
        <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Remove staff member</DialogTitle>
            <DialogDescription>
              {staffToRemove
                ? `This will revoke all Backoffice access for ${staffToRemove.email_address}. It does not delete their identity or workspace accounts.`
                : 'This will revoke Backoffice access.'}
            </DialogDescription>
          </DialogHeader>

          {staffToRemove?.assignment_counts && staffToRemove.assignment_counts.total_active > 0 && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              This staff member still has {staffToRemove.assignment_counts.total_active} active assignment{staffToRemove.assignment_counts.total_active === 1 ? '' : 's'}. Existing assignments remain assigned until an administrator reassigns them.
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 pl-1 text-destructive underline"
                onClick={() => router.visit(`/backoffice/staff/${staffToRemove.id}/assignments`)}
              >
                Manage assignments first.
              </Button>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setStaffToRemove(null)} disabled={removing}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={removeStaff} disabled={removing}>
              {removing ? 'Removing...' : 'Revoke access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BackofficeLayout>
  )
}
